import React, { useState, useEffect, useCallback } from 'react';
import { jobMaterialsAPI, vansAPI, warehouseAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Loader2, AlertTriangle, Search, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * QuickAddMaterialModal — Stage 4.5A
 * Launched from Dispatch event cards and Job detail header.
 * Auto-defaults source to the assigned tech's van when possible.
 */
export function QuickAddMaterialModal({ jobId, open, onClose, onAdded }) {
  const { isOwner } = useAuth();
  const [sourceType, setSourceType] = useState('vehicle');
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [qty, setQty] = useState('');
  const [availableQty, setAvailableQty] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [defaultLoaded, setDefaultLoaded] = useState(false);

  // Load vehicles, warehouses, and default source
  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, wRes, defRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          warehouseAPI.listWarehouses(),
          jobMaterialsAPI.defaultSource(jobId).catch(() => null),
        ]);
        const v = vRes.data?.vans || vRes.data || [];
        const w = wRes.data?.warehouses || wRes.data || [];
        setVehicles(v);
        setWarehouses(w);

        // Apply default source
        if (defRes?.data && defRes.data.source_id && defRes.data.reason !== 'none') {
          setSourceType(defRes.data.source_type);
          setSelectedSourceId(defRes.data.source_id);
        }
        setDefaultLoaded(true);
      } catch { setDefaultLoaded(true); }
    };
    if (open) load();
  }, [open, jobId]);

  // Item search with debounce
  useEffect(() => {
    if (!itemSearch || itemSearch.length < 2) { setItemResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await itemsAPI.search(itemSearch, 15);
        setItemResults(res.data?.items || res.data || []);
      } catch { /* ignore */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearch]);

  // Check available quantity
  useEffect(() => {
    if (!selectedItem || !selectedSourceId) { setAvailableQty(null); return; }
    const check = async () => {
      setLoadingAvail(true);
      try {
        if (sourceType === 'vehicle') {
          const res = await vansAPI.getInventory(selectedSourceId);
          const inv = (res.data?.inventory || res.data || []).find(i => i.item_id === selectedItem.id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.quarantined_quantity || 0)) : 0);
        } else {
          const res = await warehouseAPI.getInventory({ location_id: selectedSourceId });
          const inv = (res.data?.items || res.data?.inventory || res.data || []).find(i => i.item_id === selectedItem.id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.reserved_quantity || 0) - (inv.quarantined_quantity || 0)) : 0);
        }
      } catch { setAvailableQty(null); }
      setLoadingAvail(false);
    };
    check();
  }, [selectedItem, selectedSourceId, sourceType]);

  const handleSubmit = async () => {
    if (!selectedItem) { toast.error('Select an item'); return; }
    if (!selectedSourceId) { toast.error('Select a source'); return; }
    const parsedQty = parseInt(qty, 10);
    if (!qty || isNaN(parsedQty) || parsedQty < 1) { toast.error('Qty must be a positive integer'); return; }

    setSubmitting(true);
    const idempotencyKey = `qa_${jobId}_${selectedItem.id}_${Date.now()}`;
    try {
      const res = await jobMaterialsAPI.add(jobId, {
        item_id: selectedItem.id,
        qty: parsedQty,
        source_type: sourceType,
        source_vehicle_id: sourceType === 'vehicle' ? selectedSourceId : undefined,
        source_warehouse_id: sourceType === 'warehouse' ? selectedSourceId : undefined,
        idempotency_key: idempotencyKey,
      });

      const matId = res.data?.id;
      const sourceName = res.data?.source_name || (sourceType === 'vehicle' ? vehicles.find(v => v.id === selectedSourceId)?.name : warehouses.find(w => w.id === selectedSourceId)?.name);

      // Toast with undo
      toast.success(
        `Added ${parsedQty}x ${selectedItem.name} — deducted from ${sourceType === 'vehicle' ? 'Van' : 'Warehouse'}: ${sourceName || 'source'}`,
        {
          action: matId ? {
            label: 'Undo',
            onClick: async () => {
              try {
                await jobMaterialsAPI.reverse(jobId, matId);
                toast.success('Material reversed — stock restored');
                if (onAdded) onAdded();
              } catch { toast.error('Failed to undo'); }
            },
          } : undefined,
          duration: 8000,
        }
      );

      if (onAdded) onAdded();
      // Reset for next quick-add
      setSelectedItem(null);
      setItemSearch('');
      setQty('');
      setAvailableQty(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'INSUFFICIENT_STOCK') {
        toast.error(`Insufficient stock. Available: ${detail.available}, Requested: ${detail.requested}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to add material');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sources = sourceType === 'vehicle' ? vehicles : warehouses;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="quick-add-material-modal">
        <DialogHeader>
          <DialogTitle>Quick Add Material</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSelectedSourceId(''); setAvailableQty(null); }}>
                <SelectTrigger data-testid="qa-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicle">Vehicle</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{sourceType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</Label>
              <Select value={selectedSourceId} onValueChange={(v) => { setSelectedSourceId(v); setAvailableQty(null); }}>
                <SelectTrigger data-testid="qa-source-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Item search */}
          <div>
            <Label className="text-xs">Item</Label>
            {selectedItem ? (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                <span className="text-sm font-medium flex-1 truncate" data-testid="qa-selected-item">{selectedItem.name}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelectedItem(null); setItemSearch(''); setAvailableQty(null); }}>
                  <span className="text-lg leading-none">&times;</span>
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search items..." className="pl-8" data-testid="qa-item-search" />
                {searching && <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
                {itemResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-40 overflow-auto">
                    {itemResults.map((item) => (
                      <button key={item.id} className="w-full text-left px-3 py-2 hover:bg-accent text-sm" onClick={() => { setSelectedItem(item); setItemSearch(''); setItemResults([]); }} data-testid={`qa-item-opt-${item.id}`}>
                        {item.name} {item.sku ? `(${item.sku})` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Available qty + qty input inline */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs">Qty</Label>
              <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="1" data-testid="qa-qty-input" />
            </div>
            {selectedItem && selectedSourceId && (
              <div className="text-sm pb-2" data-testid="qa-available-qty">
                {loadingAvail ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  availableQty != null ? (
                    <span className={availableQty > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                      Avail: {availableQty}
                    </span>
                  ) : null
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} size="sm">Close</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedItem || !selectedSourceId || !qty} size="sm" data-testid="qa-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
